// Surface 4 · Workspace (/frameworks/instances/{instanceId}).
//
// MR 5 scope: shell + Focus tab + ActivityDrawer (workspace mode).
// Timeline + Deliverables tabs render "coming in next release" placeholders;
// their full implementations land in MR 7 + MR 8 (V1 proper).
//
// Focus is the default tab per handoff shaping decision #2.
// Drift is client-computed every render per engineering-answers Q2.
//
// InstanceRail (sidebar switcher) is deferred to MR 6 polish — the
// single-instance case works fine without it.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  Activity,
  ActivityId,
  ActivityState,
  ActivityStateEntry,
  FrameworkDetail,
  FrameworkInstance,
  Phase,
} from '@gc-coach/shared';
import {
  createCoachSession,
  getMeFrameworkInstance,
  patchInstanceActivity,
} from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import { ActivityDrawer } from './ActivityDrawer.js';
import { driftingActivities, todayIsoUtc } from './drift.js';
import styles from './FrameworkWorkspacePage.module.css';

type Tab = 'focus' | 'timeline' | 'deliverables';

export function FrameworkWorkspacePage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const { t, tt } = useI18n();

  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'notfound' }
    | { kind: 'error'; message: string }
    | { kind: 'ok'; instance: FrameworkInstance; framework: FrameworkDetail }
  >({ kind: 'loading' });
  const [tab, setTab] = useState<Tab>('focus');
  const [selectedActivityId, setSelectedActivityId] = useState<ActivityId | null>(null);
  const [pendingPatch, setPendingPatch] = useState(false);
  const [askingCoach, setAskingCoach] = useState(false);

  async function askTheCoach() {
    if (!instanceId || askingCoach) return;
    setAskingCoach(true);
    const result = await createCoachSession({ frameworkInstanceId: instanceId });
    setAskingCoach(false);
    if (result.ok) {
      navigate('/coach');
    } else {
      // Non-blocking failure — surface in the header meta for now; the
      // Coach surface rebuild (MR B) adds proper error-banner UI.
      // eslint-disable-next-line no-alert
      alert(t('frameworks.workspace.askCoachError'));
    }
  }

  const load = useCallback(async () => {
    if (!instanceId) {
      setState({ kind: 'notfound' });
      return;
    }
    const result = await getMeFrameworkInstance(instanceId);
    if (!result.ok) {
      if (result.code === 'NOT_FOUND') {
        setState({ kind: 'notfound' });
      } else {
        setState({ kind: 'error', message: t('frameworks.library.errorGeneric') });
      }
      return;
    }
    setState({
      kind: 'ok',
      instance: result.data.instance,
      framework: result.data.framework,
    });
  }, [instanceId, t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) {
        /* no-op */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  // ─── Loading / error / notfound ──────────────────────────────────────
  if (state.kind === 'loading') {
    return (
      <div className={styles.pad}>
        <div className={styles.wrap}>
          <div className={styles.skeletonHeader} />
          <div className={styles.skeletonHero} />
          <div className={styles.skeletonCard} />
        </div>
      </div>
    );
  }
  if (state.kind === 'notfound') {
    return (
      <div className={styles.pad}>
        <div className={styles.wrap}>
          <div className={styles.messagePanel}>
            <h3 className={styles.messageTitle}>{t('frameworks.workspace.notFoundTitle')}</h3>
            <p>{t('frameworks.workspace.notFoundBody')}</p>
            <button
              type="button"
              className={styles.messageButton}
              onClick={() => navigate('/frameworks')}
            >
              {t('frameworks.preview.backToLibrary')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={styles.pad}>
        <div className={styles.wrap}>
          <div className={styles.messagePanel}>
            <p>{state.message}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Derivations for the Focus tab ───────────────────────────────────
  const { instance, framework } = state;
  const today = todayIsoUtc();

  // Flatten + build lookup maps.
  const activityById = new Map<ActivityId, { activity: Activity; phase: Phase; phaseIndex: number }>();
  framework.phases.forEach((phase, pi) => {
    for (const a of phase.activities) {
      activityById.set(a.id, { activity: a, phase, phaseIndex: pi + 1 });
    }
  });
  const stateById = new Map<ActivityId, ActivityStateEntry>(
    instance.activity_states.map((s) => [s.activity_id, s]),
  );
  const plannedById = new Map(instance.activity_planned_dates.map((p) => [p.activity_id, p]));

  // Blocked = any dep is not complete. "Blocked" is a condition, not a state.
  function isBlocked(a: Activity): boolean {
    const deps = a.dependencies ?? [];
    return deps.some((d) => stateById.get(d.activity_id)?.state !== 'complete');
  }

  const inProgress: ActivityStateEntry[] = [];
  const awaitingReview: ActivityStateEntry[] = [];
  const ready: ActivityStateEntry[] = [];
  const blocked: ActivityStateEntry[] = [];

  for (const s of instance.activity_states) {
    const meta = activityById.get(s.activity_id);
    if (!meta) continue;
    if (s.state === 'complete') continue;
    if (s.state === 'in_progress') inProgress.push(s);
    else if (s.state === 'awaiting_review') awaitingReview.push(s);
    else if (isBlocked(meta.activity)) blocked.push(s);
    else ready.push(s);
  }

  // "Do this next" picks the earliest in-progress by planned_start; falls
  // back to earliest ready; falls back to nothing (all done or all blocked).
  const nextUp: ActivityStateEntry | null = (() => {
    const byPlannedStart = (a: ActivityStateEntry, b: ActivityStateEntry) => {
      const pa = plannedById.get(a.activity_id)?.planned_start ?? '9999-99-99';
      const pb = plannedById.get(b.activity_id)?.planned_start ?? '9999-99-99';
      return pa.localeCompare(pb);
    };
    if (inProgress.length > 0) return [...inProgress].sort(byPlannedStart)[0] ?? null;
    if (ready.length > 0) return [...ready].sort(byPlannedStart)[0] ?? null;
    return null;
  })();

  const drifting = driftingActivities(instance, today);
  const progressComplete = instance.activity_states.filter((s) => s.state === 'complete').length;
  const progressTotal = instance.activity_states.length;
  const progressPct =
    progressTotal === 0 ? 0 : Math.round((progressComplete / progressTotal) * 100);

  // ─── Activity drawer plumbing ────────────────────────────────────────
  const selectedMeta = selectedActivityId ? activityById.get(selectedActivityId) : null;
  const selectedState = selectedActivityId ? stateById.get(selectedActivityId) ?? null : null;
  const selectedBlocked = selectedMeta ? isBlocked(selectedMeta.activity) : false;
  const selectedDepTitles =
    selectedMeta?.activity.dependencies?.map(
      (d) => activityById.get(d.activity_id)?.activity.title ?? d.activity_id,
    ) ?? [];

  async function onTransition(next: ActivityState) {
    if (!selectedActivityId || !instanceId) return;
    setPendingPatch(true);
    const result = await patchInstanceActivity(instanceId, selectedActivityId, { state: next });
    setPendingPatch(false);
    if (result.ok) {
      setState({ kind: 'ok', instance: result.data.instance, framework });
    }
  }
  async function onAddNote(note: string) {
    if (!selectedActivityId || !instanceId) return;
    setPendingPatch(true);
    const result = await patchInstanceActivity(instanceId, selectedActivityId, { note });
    setPendingPatch(false);
    if (result.ok) {
      setState({ kind: 'ok', instance: result.data.instance, framework });
    }
  }

  return (
    <div className={styles.pad}>
      <div className={styles.wrap}>
        <Link to="/frameworks" className={styles.backLink}>
          ← {t('frameworks.preview.backToLibrary')}
        </Link>

        {/* ─── Instance header ────────────────────────────────────── */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>{instance.name}</h1>
            <div className={styles.headerMeta}>
              <span className={styles.metaItem}>
                <strong>
                  {progressComplete}/{progressTotal}
                </strong>{' '}
                {t('frameworks.workspace.activitiesLabel')}
              </span>
              <span className={styles.metaItem}>
                <strong>{progressPct}%</strong>
              </span>
              <span className={styles.metaItem}>
                {t(`frameworks.domain.${framework.domain}`)}
              </span>
            </div>
            <div className={styles.progressBar}>
              <span className={styles.progressFill} style={{ width: `${progressPct}%` }} />
            </div>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.askCoachButton}
              onClick={() => void askTheCoach()}
              disabled={askingCoach}
            >
              ✦{' '}
              {askingCoach
                ? t('frameworks.workspace.askCoachOpening')
                : t('frameworks.workspace.askCoachCta')}
            </button>
          </div>
        </header>

        {/* ─── Tabs ──────────────────────────────────────────────── */}
        <div className={styles.tabs} role="tablist">
          <TabButton tab="focus" active={tab} setActive={setTab} label={t('frameworks.workspace.tabFocus')} />
          <TabButton tab="timeline" active={tab} setActive={setTab} label={t('frameworks.workspace.tabTimeline')} />
          <TabButton
            tab="deliverables"
            active={tab}
            setActive={setTab}
            label={t('frameworks.workspace.tabDeliverables')}
          />
        </div>

        {/* ─── Tab content ───────────────────────────────────────── */}
        {tab === 'focus' && (
          <>
            {/* Next up hero */}
            {nextUp ? (
              <div className={styles.hero}>
                <div className={styles.heroEyebrow}>{t('frameworks.workspace.doThisNext')}</div>
                <div className={styles.heroTitle}>
                  {activityById.get(nextUp.activity_id)?.activity.title}
                </div>
                <div className={styles.heroDescription}>
                  {activityById.get(nextUp.activity_id)?.activity.description}
                </div>
                <div className={styles.heroMetaRow}>
                  <span>
                    {t('frameworks.preview.phaseLabel')}{' '}
                    {activityById.get(nextUp.activity_id)?.phaseIndex}
                  </span>
                  {activityById.get(nextUp.activity_id)?.activity.duration?.default_months && (
                    <>
                      <span>·</span>
                      <span>
                        {t(
                          `frameworks.duration.${activityById.get(nextUp.activity_id)!.activity.duration!.default_months}`,
                        )}
                      </span>
                    </>
                  )}
                </div>
                <div className={styles.heroCtaRow}>
                  <button
                    type="button"
                    className={styles.heroPrimary}
                    onClick={() => setSelectedActivityId(nextUp.activity_id)}
                  >
                    {t('frameworks.workspace.openActivity')}
                  </button>
                  <Link to="/coach" className={styles.heroGhost}>
                    {t('frameworks.preview.ctaAskCoach')}
                  </Link>
                </div>
              </div>
            ) : (
              <div className={styles.messagePanel}>
                <h3 className={styles.messageTitle}>
                  {progressComplete === progressTotal
                    ? t('frameworks.workspace.allDoneTitle')
                    : t('frameworks.workspace.nothingReadyTitle')}
                </h3>
                <p>
                  {progressComplete === progressTotal
                    ? t('frameworks.workspace.allDoneBody')
                    : t('frameworks.workspace.nothingReadyBody')}
                </p>
              </div>
            )}

            {/* Drift card */}
            {drifting.length > 0 && (
              <section className={styles.driftCard}>
                <div className={styles.driftHeader}>
                  ⚠ {t('frameworks.workspace.driftHeader')}
                </div>
                <ul className={styles.driftList}>
                  {drifting.slice(0, 3).map((d) => {
                    const meta = activityById.get(d.activity_id);
                    const planned = plannedById.get(d.activity_id);
                    if (!meta) return null;
                    return (
                      <li key={d.activity_id} className={styles.driftItem}>
                        <button
                          type="button"
                          className={styles.driftButton}
                          onClick={() => setSelectedActivityId(d.activity_id)}
                        >
                          <strong>{meta.activity.title}</strong>{' '}
                          {planned && (
                            <span className={styles.driftNote}>
                              {tt('frameworks.workspace.driftPlannedTemplate', {
                                date: planned.planned_end,
                              })}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {/* Awaiting review */}
            {awaitingReview.length > 0 && (
              <ActivityBucket
                label={t('frameworks.workspace.awaitingReviewLabel')}
                states={awaitingReview}
                activityById={activityById}
                onSelect={setSelectedActivityId}
              />
            )}

            {/* Ready */}
            {ready.length > 0 && (
              <ActivityBucket
                label={t('frameworks.workspace.readyLabel')}
                states={ready.filter((s) => s.activity_id !== nextUp?.activity_id)}
                activityById={activityById}
                onSelect={setSelectedActivityId}
              />
            )}

            {/* Blocked */}
            {blocked.length > 0 && (
              <ActivityBucket
                label={t('frameworks.workspace.blockedLabel')}
                states={blocked}
                activityById={activityById}
                onSelect={setSelectedActivityId}
                variant="blocked"
                depTitlesForId={(id) => {
                  const meta = activityById.get(id);
                  if (!meta) return [];
                  return (
                    meta.activity.dependencies
                      ?.filter((d) => stateById.get(d.activity_id)?.state !== 'complete')
                      .map((d) => activityById.get(d.activity_id)?.activity.title ?? d.activity_id) ?? []
                  );
                }}
              />
            )}
          </>
        )}

        {tab === 'timeline' && (
          <div className={styles.messagePanel}>
            <h3 className={styles.messageTitle}>{t('frameworks.workspace.timelinePlaceholderTitle')}</h3>
            <p>{t('frameworks.workspace.timelinePlaceholderBody')}</p>
          </div>
        )}

        {tab === 'deliverables' && (
          <div className={styles.messagePanel}>
            <h3 className={styles.messageTitle}>{t('frameworks.workspace.deliverablesPlaceholderTitle')}</h3>
            <p>{t('frameworks.workspace.deliverablesPlaceholderBody')}</p>
          </div>
        )}
      </div>

      {/* ─── Activity drawer (workspace mode) ──────────────────────── */}
      <ActivityDrawer
        open={selectedActivityId !== null}
        onClose={() => setSelectedActivityId(null)}
        phase={selectedMeta?.phase ?? null}
        phaseIndex={selectedMeta?.phaseIndex ?? 0}
        activity={selectedMeta?.activity ?? null}
        state={selectedState}
        dependencyTitles={selectedDepTitles}
        blocked={selectedBlocked}
        onTransition={onTransition}
        onAddNote={onAddNote}
        pending={pendingPatch}
        onAskCoach={() => void askTheCoach()}
        askingCoach={askingCoach}
      />
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function TabButton({
  tab,
  active,
  setActive,
  label,
}: {
  tab: Tab;
  active: Tab;
  setActive: (t: Tab) => void;
  label: string;
}) {
  const isActive = active === tab;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      className={isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab}
      onClick={() => setActive(tab)}
    >
      {label}
    </button>
  );
}

interface ActivityBucketProps {
  label: string;
  states: ReadonlyArray<ActivityStateEntry>;
  activityById: Map<ActivityId, { activity: Activity; phase: Phase; phaseIndex: number }>;
  onSelect: (id: ActivityId) => void;
  variant?: 'blocked';
  depTitlesForId?: (id: ActivityId) => ReadonlyArray<string>;
}

function ActivityBucket(props: ActivityBucketProps) {
  const { t } = useI18n();
  return (
    <section className={styles.bucket}>
      <div className={styles.bucketLabel}>{props.label}</div>
      <ul className={styles.bucketList}>
        {props.states.map((s) => {
          const meta = props.activityById.get(s.activity_id);
          if (!meta) return null;
          const depTitles = props.depTitlesForId ? props.depTitlesForId(s.activity_id) : [];
          return (
            <li
              key={s.activity_id}
              className={
                props.variant === 'blocked'
                  ? `${styles.bucketItem} ${styles.bucketItemBlocked}`
                  : styles.bucketItem
              }
            >
              <button
                type="button"
                className={styles.bucketItemBtn}
                onClick={() => props.onSelect(s.activity_id)}
              >
                <div className={styles.bucketItemName}>{meta.activity.title}</div>
                <div className={styles.bucketItemMeta}>
                  {t('frameworks.preview.phaseLabel')} {meta.phaseIndex}
                  {depTitles.length > 0 && (
                    <>
                      {' · '}
                      {t('frameworks.workspace.needsPrefix')} {depTitles.join(', ')}
                    </>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
